import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ArrowUpRight,
  Award,
  BookOpen,
  ChevronRight,
  Globe2,
  GraduationCap,
  HelpCircle,
  Home,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Package,
  Phone,
  ShieldCheck,
  ShoppingBag,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import LoginModal from '../auth/LoginModal';
import { marketingHref } from '../../lib/surface';
import logo from '../assets/logo.png';

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  external?: boolean;
}

const studyNav: NavItem[] = [
  { label: 'Overview', to: '/dashboard', icon: LayoutDashboard },
  { label: 'Programmes', to: '/courses', icon: BookOpen },
  { label: 'Certificates', to: '/certificates', icon: Award },
];

const publicNav: NavItem[] = [
  { label: 'Programmes', to: marketingHref('/courses'), icon: BookOpen, external: true },
  { label: 'Verify certificate', to: '/verify', icon: ShieldCheck },
];

const guestExploreNav: NavItem[] = [
  { label: 'Home', to: marketingHref('/'), icon: Home, external: true },
  { label: 'Programmes', to: marketingHref('/courses'), icon: BookOpen, external: true },
  { label: 'Short courses', to: marketingHref('/short-courses'), icon: ShoppingBag, external: true },
  { label: 'Contact', to: marketingHref('/contact'), icon: Phone, external: true },
];

const guestProgrammeNav: NavItem[] = [
  { label: 'Foodstuff Export Masterclass', to: marketingHref('/fem'), icon: Package, external: true },
  { label: 'Zero2Export', to: marketingHref('/zero2export'), icon: Globe2, external: true },
  { label: 'Export Mentorship Program', to: marketingHref('/emp'), icon: Users, external: true },
  { label: 'Campus2Container', to: marketingHref('/campus2container'), icon: GraduationCap, external: true },
];

const guestRecordsNav: NavItem[] = [{ label: 'Verify certificate', to: '/verify', icon: ShieldCheck }];

const accountNav: NavItem[] = [{ label: 'Profile & settings', to: '/account', icon: UserRound }];

const isItemActive = (pathname: string, to: string) => {
  if (to.startsWith('http')) return false;
  if (to === '/dashboard') return pathname === '/dashboard';
  if (to === '/courses') return pathname === '/courses' || pathname.startsWith('/course/');
  if (to === '/verify') return pathname === '/verify' || pathname.startsWith('/verify/');
  return pathname === to || pathname.startsWith(`${to}/`);
};

const NavGroup: React.FC<{
  label: string;
  items: NavItem[];
  pathname: string;
  onNavigate?: () => void;
}> = ({ label, items, pathname, onNavigate }) => (
  <div>
    <p className="px-3 text-[9px] font-medium uppercase tracking-[0.17em] text-slate-400">{label}</p>
    <ul className="mt-2 space-y-1">
      {items.map((item) => {
        const active = isItemActive(pathname, item.to);
        const className = `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[12.5px] font-medium transition-all ${
          active
            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
            : 'text-slate-600 hover:bg-blue-50 hover:text-blue-800'
        }`;
        const icon = (
          <item.icon
            className={`h-[17px] w-[17px] flex-none transition-colors ${
              active ? 'text-white' : 'text-slate-400 group-hover:text-blue-600'
            }`}
            strokeWidth={1.7}
          />
        );
        const content = (
          <>
            {icon}
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {active && <ChevronRight className="h-3.5 w-3.5 opacity-70" strokeWidth={2} />}
          </>
        );
        return (
          <li key={`${label}-${item.to}`}>
            {item.external ? (
              <a href={item.to} onClick={onNavigate} className={className}>
                {content}
              </a>
            ) : (
              <NavLink
                to={item.to}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={className}
              >
                {content}
              </NavLink>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

const Initials: React.FC<{ name?: string | null; email?: string | null; dark?: boolean }> = ({
  name,
  email,
  dark,
}) => {
  const initials =
    (name || email || 'LS')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'LS';
  return (
    <span
      className={`flex h-9 w-9 flex-none items-center justify-center rounded-xl font-mono text-[10px] font-semibold tracking-wider ${
        dark ? 'bg-white/15 text-white ring-1 ring-white/20' : 'bg-blue-100 text-blue-700'
      }`}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

const LmsShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAdmin, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const hideMobileNav = ['/login', '/signup', '/forgot-password', '/reset-password'].includes(location.pathname);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('lms-has-bottom-nav', !hideMobileNav);
    return () => document.body.classList.remove('lms-has-bottom-nav');
  }, [hideMobileNav]);

  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const routeLabel = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Overview';
    if (path.startsWith('/learn/')) return 'Classroom';
    if (path === '/courses' || path.startsWith('/course/')) return 'Programmes';
    if (path === '/certificates') return 'Certificates';
    if (path === '/account') return 'Profile & settings';
    if (path.startsWith('/admin')) return 'Admin console';
    if (path.startsWith('/verify')) return 'Certificate register';
    return 'Learning console';
  };

  const brandBlock = (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-800 via-blue-700 to-sky-500 p-4 text-white shadow-xl shadow-blue-700/20">
      <span aria-hidden="true" className="absolute -right-7 -top-9 h-24 w-24 rounded-full border border-white/15" />
      <span aria-hidden="true" className="absolute -bottom-12 -left-8 h-28 w-28 rounded-full border border-white/10" />
      <div className="relative flex items-center gap-3">
        <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white p-1.5 shadow-sm">
          <img src={logo} alt="" className="h-full w-full object-contain" />
        </span>
        <span className="leading-none">
          <span className="block text-[16px] font-semibold tracking-[0.14em]">LSEM</span>
          <span className="mt-1.5 block font-mono text-[8px] uppercase tracking-[0.16em] text-blue-100/80">
            Learning
          </span>
        </span>
      </div>
      <p className="relative mt-4 max-w-[22ch] text-[11px] leading-4 text-blue-100/75">
        Your programmes, progress and certificates in one place.
      </p>
    </div>
  );

  const navBody = (
    <>
      <div className="mt-7 flex-1 space-y-8 overflow-y-auto pb-2">
        {isAuthenticated ? (
          <NavGroup
            label="Study"
            items={studyNav}
            pathname={location.pathname}
            onNavigate={() => setMenuOpen(false)}
          />
        ) : (
          <>
            <NavGroup
              label="Explore LSEM"
              items={guestExploreNav}
              pathname={location.pathname}
              onNavigate={() => setMenuOpen(false)}
            />
            <NavGroup
              label="Scale-up programmes"
              items={guestProgrammeNav}
              pathname={location.pathname}
              onNavigate={() => setMenuOpen(false)}
            />
            <NavGroup
              label="Student records"
              items={guestRecordsNav}
              pathname={location.pathname}
              onNavigate={() => setMenuOpen(false)}
            />
          </>
        )}
        {isAdmin && (
          <NavGroup
            label="Administration"
            items={[{ label: 'Admin console', to: '/admin', icon: ShieldCheck }]}
            pathname={location.pathname}
            onNavigate={() => setMenuOpen(false)}
          />
        )}
        {isAuthenticated && (
          <NavGroup
            label="Account"
            items={accountNav}
            pathname={location.pathname}
            onNavigate={() => setMenuOpen(false)}
          />
        )}
      </div>

      <div className="mt-4 border-t border-rule pt-4">
        <a
          href={marketingHref('/')}
          className="flex items-center justify-between rounded-xl px-3 py-2 text-[11px] font-medium text-slate-500 transition-colors hover:bg-blue-50 hover:text-blue-700"
        >
          lsem.org.ng
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.6} />
        </a>
        {isAuthenticated ? (
          <div className="mt-2 flex items-center gap-2.5 rounded-xl border border-rule bg-paper/80 px-3 py-2.5">
            <Initials name={user?.name} email={user?.email} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-semibold text-ink">{user?.name || 'Student'}</span>
              <span className="mt-0.5 block truncate font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2">
                {isAdmin ? 'Administrator' : 'Student'}
              </span>
            </span>
            <button
              type="button"
              onClick={() => void logout()}
              aria-label="Sign out"
              title="Sign out"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white hover:text-blue-700"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.7} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setLoginOpen(true)}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-[12px] font-semibold text-white shadow-sm shadow-blue-600/20 transition-colors hover:bg-blue-700"
          >
            <LogIn className="h-4 w-4" strokeWidth={1.7} /> Sign in
          </button>
        )}
      </div>
    </>
  );

  const bottomNav: NavItem[] = isAuthenticated
    ? isAdmin
      ? [
          studyNav[0],
          studyNav[1],
          { label: 'Admin', to: '/admin', icon: ShieldCheck },
          { label: 'Profile', to: '/account', icon: UserRound },
        ]
      : [...studyNav, { label: 'Profile', to: '/account', icon: UserRound }]
    : publicNav;

  return (
    <div className="lms-root min-h-screen">
      <a
        href="#lms-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[80] focus:rounded-xl focus:bg-white focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-blue-700 focus:shadow-xl"
      >
        Skip to main content
      </a>

      <div className="lg:grid lg:min-h-screen lg:grid-cols-[264px_minmax(0,1fr)]">
        <aside
          className="sticky top-0 hidden h-screen flex-col overflow-y-auto border-r border-rule/80 bg-white/90 px-4 py-5 backdrop-blur-xl lg:flex"
          aria-label="Learning console"
        >
          {brandBlock}
          {navBody}
        </aside>

        <div className="min-w-0">
          <header className="sticky top-0 z-30 border-b border-rule/80 bg-white/80 backdrop-blur-xl">
            <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-7 lg:px-10">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMenuOpen(true)}
                  className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-rule bg-white text-ink shadow-sm transition-colors hover:border-blue-200 hover:text-blue-700 lg:hidden"
                  aria-label="Open navigation"
                  aria-expanded={menuOpen}
                >
                  <Menu className="h-4 w-4" strokeWidth={1.8} />
                </button>
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-blue-50 p-1.5 lg:hidden">
                  <img src={logo} alt="" className="h-full w-full object-contain" />
                </span>
                <div className="min-w-0">
                  <p className="hidden text-[9px] font-medium uppercase tracking-[0.16em] text-slate-400 sm:block">
                    Learning console
                  </p>
                  <p className="truncate text-[13.5px] font-semibold tracking-[-0.01em] text-ink">{routeLabel()}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-4">
                <span className="hidden font-mono text-[9px] uppercase tracking-[0.13em] text-slate-400 md:block">
                  2026 session · Lagos
                </span>
                <a
                  href="mailto:info@lsem.org.ng"
                  className="hidden items-center gap-1.5 text-[11.5px] font-medium text-slate-500 transition-colors hover:text-blue-700 sm:inline-flex"
                >
                  <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.7} />
                  Student desk
                </a>
                {isAuthenticated ? (
                  <div className="flex items-center gap-2">
                    <Initials name={user?.name} email={user?.email} />
                    <span className="hidden text-[12px] font-semibold text-ink sm:block">
                      {user?.name?.trim().split(/\s+/)[0] || 'Student'}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setLoginOpen(true)}
                    className="lms-btn-secondary !min-h-0 !px-3 !py-2 !text-[11.5px]"
                  >
                    <LogIn className="h-3.5 w-3.5" strokeWidth={1.7} /> Sign in
                  </button>
                )}
              </div>
            </div>
          </header>

          <main
            id="lms-main"
            className={`px-4 pt-6 sm:px-7 lg:px-10 lg:pt-9 lg:pb-12 ${
              hideMobileNav ? 'pb-12' : 'pb-28'
            }`}
          >
            <div className="mx-auto max-w-[1240px]">{children}</div>
          </main>
        </div>
      </div>

      {!hideMobileNav && (
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-rule/90 bg-white/95 px-2 pb-[calc(env(safe-area-inset-bottom)+0.35rem)] pt-1.5 shadow-[0_-12px_30px_-24px_rgba(15,40,90,0.35)] backdrop-blur-xl lg:hidden"
        aria-label="Quick navigation"
      >
        <div className={`mx-auto grid max-w-md ${isAuthenticated ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {bottomNav.map((item) => {
            const active = isItemActive(location.pathname, item.to);
            const className = `flex flex-col items-center gap-1 rounded-xl px-2 py-1.5 text-[9px] font-semibold transition-colors ${
              active ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:bg-blue-50/70 hover:text-blue-700'
            }`;
            const content = (
              <>
                <item.icon className="h-[18px] w-[18px]" strokeWidth={active ? 2 : 1.7} />
                <span className="truncate">{item.label}</span>
              </>
            );
            return item.external ? (
              <a key={item.to} href={item.to} className={className}>
                {content}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                aria-current={active ? 'page' : undefined}
                className={className}
              >
                {content}
              </NavLink>
            );
          })}
          {!isAuthenticated && (
            <button
              type="button"
              onClick={() => setLoginOpen(true)}
              className="flex flex-col items-center gap-1 rounded-xl px-2 py-1.5 text-[9px] font-semibold text-slate-500 transition-colors hover:bg-blue-50/70 hover:text-blue-700"
            >
              <LogIn className="h-[18px] w-[18px]" strokeWidth={1.7} />
              <span>Sign in</span>
            </button>
          )}
        </div>
      </nav>
      )}

      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Learning navigation"
            className="relative flex h-full w-[min(88vw,340px)] flex-col overflow-y-auto border-r border-rule bg-white px-4 py-5 shadow-2xl"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 p-1.5">
                  <img src={logo} alt="" className="h-full w-full object-contain" />
                </span>
                <span>
                  <span className="block text-[13px] font-semibold tracking-[0.12em] text-ink">LSEM</span>
                  <span className="mt-0.5 block font-mono text-[8px] uppercase tracking-[0.14em] text-ink-2">
                    Learning
                  </span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-rule text-slate-500 transition-colors hover:border-blue-200 hover:text-blue-700"
                aria-label="Close navigation"
              >
                <X className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </div>
            {navBody}
          </div>
        </div>
      )}

      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
};

export default LmsShell;
